import React from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const ContentLibrary = ({ content, selectedCategory, viewMode, searchQuery, filters, onContentSelect }) => {
  // Filter content based on category, search, and filters
  const filteredContent = content?.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item?.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      item?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesType = filters?.type === 'all' || item?.type === filters?.type;
    const matchesLevel = filters?.level === 'all' || item?.level === filters?.level;
    const matchesFormat = filters?.format === 'all' || item?.format === filters?.format;
    const matchesStatus = filters?.status === 'all' || item?.status === filters?.status;

    return matchesCategory && matchesSearch && matchesType && matchesLevel && matchesFormat && matchesStatus;
  }) || [];

  const getFormatIcon = (format) => {
    switch (format) {
      case 'video':
        return 'Video';
      case 'document':
        return 'FileText';
      case 'interactive':
        return 'MousePointer';
      default:
        return 'File';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-500/10 text-green-500';
      case 'intermediate':
        return 'bg-blue-500/10 text-blue-500';
      case 'advanced':
        return 'bg-purple-500/10 text-purple-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContent?.map((item) => (
          <div
            key={item?.id}
            onClick={() => onContentSelect(item)}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer"
          >
            <div className="relative h-40 bg-muted">
              <AppImage
                src={item?.thumbnail}
                alt={item?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(item?.level)}`}>
                  {item?.level}
                </span>
                {item?.hasAssessment && (
                  <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                    Quiz
                  </span>
                )}
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-base font-semibold text-foreground line-clamp-1">{item?.title}</h3>
                <Icon name={getFormatIcon(item?.format)} size={18} className="text-muted-foreground flex-shrink-0" />
              </div>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item?.description}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Icon name="Clock" size={14} />
                  {item?.duration}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Users" size={14} />
                  {item?.completions}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Star" size={14} className="text-yellow-500" />
                  {item?.rating}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-1 bg-muted rounded text-muted-foreground">v{item?.version}</span>
                <span className="text-muted-foreground">{item?.lastUpdated}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-3">
      {filteredContent?.map((item) => (
        <div
          key={item?.id}
          onClick={() => onContentSelect(item)}
          className="bg-card border border-border rounded-lg p-4 hover:shadow-lg transition-all cursor-pointer"
        >
          <div className="flex items-start gap-4">
            <div className="relative w-32 h-24 bg-muted rounded-lg overflow-hidden flex-shrink-0">
              <AppImage
                src={item?.thumbnail}
                alt={item?.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground mb-1">{item?.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{item?.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2 ml-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(item?.level)}`}>
                    {item?.level}
                  </span>
                  {item?.hasAssessment && (
                    <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                      Quiz
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                <span className="flex items-center gap-1">
                  <Icon name={getFormatIcon(item?.format)} size={14} />
                  {item?.format}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Clock" size={14} />
                  {item?.duration}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Users" size={14} />
                  {item?.completions} completions
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Star" size={14} className="text-yellow-500" />
                  {item?.rating}
                </span>
                <span className="ml-auto">v{item?.version} • {item?.lastUpdated}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContentLibrary;